import type {
  DeviceInfo,
  MeasurementPoint,
  PointReading,
  SiteNode,
} from "@energyplatform/client";
import { freshness, lateAfterFor, type Freshness } from "./overview.ts";

// Filters and states of the asset screen (prototype "Zasoby"): what a point's state is, and which points a filter
// keeps. Pure functions over the tree, so the screen and the tests share them.

/** Readiness values before acceptance: data may be missing without it being a fault. */
export const BEFORE_ACCEPTANCE: ReadonlySet<string> = new Set([
  "project",
  "config",
  "check",
]);

export type PointState = "attn" | "pre" | "ok";

/** Data state of a point: "na" when it has no device (nothing is expected), otherwise how current its data is. */
export function dataState(
  point: MeasurementPoint,
  reading: PointReading | undefined,
  now: number,
): Freshness | "na" {
  return point.binding
    ? freshness(reading?.at ?? null, now, lateAfterFor(point))
    : "na";
}

/**
 * Needs attention: a running point without current data or without a device. Before acceptance: planned or being
 * configured. Everything else is fine.
 */
export function pointState(
  point: MeasurementPoint,
  reading: PointReading | undefined,
  now: number,
): PointState {
  const data = dataState(point, reading, now);
  if (point.readiness === "retired" || point.readiness === "paused") {
    return "ok";
  }
  if (BEFORE_ACCEPTANCE.has(point.readiness)) {
    return data === "late" || data === "none"
      ? point.readiness === "project"
        ? "pre"
        : "attn"
      : "pre";
  }
  return data === "ok" ? "ok" : "attn";
}

export const VIEWS = ["all", "attn", "pre", "energy", "environment"] as const;
export type View = (typeof VIEWS)[number];

export interface Filter {
  media: ReadonlySet<string>;
  state: "all" | PointState;
}

/** Saved views of the prototype as filters. */
export function viewFilter(view: View): Filter {
  switch (view) {
    case "all":
      return { media: new Set(), state: "all" };
    case "attn":
      return { media: new Set(), state: "attn" };
    case "pre":
      return { media: new Set(), state: "pre" };
    case "energy":
      return { media: new Set(["el", "pv", "bess"]), state: "all" };
    case "environment":
      return {
        media: new Set([
          "temp",
          "humidity",
          "light",
          "pressure",
          "wind",
          "air",
        ]),
        state: "all",
      };
  }
}

export function sameFilter(a: Filter, b: Filter): boolean {
  return (
    a.state === b.state &&
    a.media.size === b.media.size &&
    [...a.media].every((m) => b.media.has(m))
  );
}

export function isFiltered(f: Filter, query: string): boolean {
  return f.media.size > 0 || f.state !== "all" || query.trim() !== "";
}

const fold = (s: string) =>
  s.toLocaleLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** A point passes when it matches the media, the state and the search text (its name or its node's path). */
export function passes(
  point: MeasurementPoint,
  state: PointState,
  f: Filter,
  query: string,
  path: string,
): boolean {
  if (f.media.size > 0 && !f.media.has(point.medium)) {
    return false;
  }
  if (f.state !== "all" && f.state !== state) {
    return false;
  }
  const q = fold(query.trim());
  return q === "" || fold(point.name).includes(q) || fold(path).includes(q);
}

/**
 * Nodes to show in the tree for a filter: those whose name matches the search, those with a passing point, and
 * every ancestor of them, so a match is never shown without its place.
 */
export function visibleNodes(
  nodes: SiteNode[],
  passing: MeasurementPoint[],
  query: string,
  filtered: boolean,
): Set<string> {
  if (!filtered) {
    return new Set(nodes.map((n) => n.id));
  }
  const parent = new Map(nodes.map((n) => [n.id, n.parentId]));
  const q = fold(query.trim());
  const keep = new Set<string>();
  const mark = (id: string | null | undefined) => {
    for (
      let n = id ?? null;
      n !== null && !keep.has(n);
      n = parent.get(n) ?? null
    ) {
      keep.add(n);
    }
  };
  for (const p of passing) {
    mark(p.nodeId);
  }
  if (q !== "") {
    for (const n of nodes) {
      if (fold(n.name).includes(q)) {
        mark(n.id);
      }
    }
  }
  return keep;
}

/** Devices of the organisation that no point uses now: they deliver data nobody looks at yet. */
export function unassignedDevices(
  devices: DeviceInfo[],
  points: MeasurementPoint[],
): DeviceInfo[] {
  const used = new Set(
    points.filter((p) => p.binding).map((p) => p.binding!.deviceId),
  );
  return devices
    .filter((d) => !used.has(d.id) && d.sources > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface Coverage {
  /** Share of the elapsed steps with a value, 0–1; null when no time has elapsed. */
  share: number | null;
  /** Runs of steps without a value, merged. */
  gaps: { from: number; to: number }[];
}

/** Which steps of [from, end) have a value, from the start times of the steps that do. */
export function coverage(
  times: number[],
  from: number,
  end: number,
  stepMs: number,
): Coverage {
  const start = Math.floor(from / stepMs) * stepMs;
  const have = new Set(times.map((t) => Math.floor(t / stepMs) * stepMs));
  const gaps: { from: number; to: number }[] = [];
  let all = 0;
  let full = 0;
  for (let s = start; s < end; s += stepMs) {
    all++;
    if (have.has(s)) {
      full++;
      continue;
    }
    const last = gaps[gaps.length - 1];
    if (last && last.to === s) {
      last.to = s + stepMs;
    } else {
      gaps.push({ from: s, to: s + stepMs });
    }
  }
  return { share: all === 0 ? null : full / all, gaps };
}
